import { AddressLink, DaoOnChainProposeCall } from 'Modules'

import abiDaoVoting from 'abi/abi-dao-voting.json'
import { getContract } from 'hooks/contract/use-contract-viem'
import { usePreparedTx } from 'hooks/dao-voting/use-prepared-tx'
import { clientWallet, publicWallet } from 'libs/viem/config'
import { logError } from 'utils/logger'

export interface DaoUpdateProposalOnChainArgs {
  contractAddress: string
  /** on-chain proposalId (uint256 as decimal string) */
  onChainProposalId: string
  /** unix seconds; 0 = voting opens immediately */
  snapshot: number
  /** unix seconds */
  deadline: number
  totalUnits: number
  quorumBps: number
  thresholdBps: number
}

export interface DaoDeleteProposalOnChainArgs {
  contractAddress: string
  onChainProposalId: string
}

/**
 * DAO Voting on-chain writes (DAO_Usage_Guide.md 3.2).
 * The BE prepares/persists the call data; the manager wallet only signs and
 * sends the tx to the governor proxy returned by the BE.
 * PTO ERC-3643 (PTOGovernor): the BE also encodes the tx (`to` + `data`) and
 * the FE sends it as is - see usePreparedTx.
 */
export const useDaoVotingContract = () => {
  const { sendPreparedTx } = usePreparedTx()

  const sendTx = async (
    address: AddressLink,
    contractAddress: string,
    functionName: string,
    args: unknown[]
  ) => {
    if (!address) throw new Error('Wallet is not connected')
    if (!contractAddress) throw new Error('DAO voting contract address is missing')

    const contract = getContract({
      contractAddress: contractAddress as `0x${string}`,
      ABI: abiDaoVoting,
    })

    try {
      const { request } = await contract.simulate[functionName](args, {
        account: address,
      })
      const txHash = await clientWallet?.writeContract(request)

      const receipt = await publicWallet.waitForTransactionReceipt({
        hash: txHash,
        confirmations: 6,
      })

      return { txHash, receipt }
    } catch (error) {
      logError(`error ${functionName} on-chain-----------------`, error)
      throw error
    }
  }

  /** proposeWithParams(params, targets, values, calldatas, description) */
  const proposeOnChain = async (address: AddressLink, call: DaoOnChainProposeCall) => {
    // PTO ERC-3643: PTOGovernor.propose already encoded by the BE
    if (call?.to && call?.data) {
      return sendPreparedTx(address, { to: call.to, data: call.data })
    }

    return sendTx(address, call?.contractAddress, 'proposeWithParams', [
      {
        collection: call.proposalParams.collection,
        snapshot: call.proposalParams.snapshot,
        deadline: call.proposalParams.deadline,
        quorumBps: call.proposalParams.quorumBps,
        thresholdBps: call.proposalParams.thresholdBps,
        totalUnits: BigInt(call.proposalParams.totalUnits),
      },
      call.targets,
      call.values.map((value) => BigInt(value)),
      call.calldatas,
      // description must match the BE record byte-for-byte (proposalId is its hash)
      call.description,
    ])
  }

  /** updateProposal(proposalId, snapshot, deadline, totalUnits, quorumBps, thresholdBps) - Pending only */
  const updateProposalOnChain = async (
    address: AddressLink,
    args: DaoUpdateProposalOnChainArgs
  ) => {
    return sendTx(address, args.contractAddress, 'updateProposal', [
      BigInt(args.onChainProposalId),
      args.snapshot,
      args.deadline,
      BigInt(args.totalUnits),
      args.quorumBps,
      args.thresholdBps,
    ])
  }

  /** deleteProposal(proposalId) - Pending only; frees the collection for a new proposal */
  const deleteProposalOnChain = async (
    address: AddressLink,
    args: DaoDeleteProposalOnChainArgs
  ) => {
    return sendTx(address, args.contractAddress, 'deleteProposal', [BigInt(args.onChainProposalId)])
  }

  return {
    proposeOnChain,
    updateProposalOnChain,
    deleteProposalOnChain,
  }
}
